import type { MemberDto } from "@ied/shared-types";
import { useState } from "react";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useCreateMembersBatch } from "@/hooks/mutations/use-create-members-batch";
import { useMemberTableContext } from "./members-table-provider";

export function Importer() {
	const { abbreviationToNameMap, groups } = useMemberTableContext();
	const [isPreviewOpen, setPreviewOpen] = useState(false);
	const [inputMembers, setInputMembers] = useState<Partial<MemberDto>[]>([]);
	const { mutate: createBatch, isPending } = useCreateMembersBatch();

	const handleImport = async () => {
		const input = document.createElement("input");
		input.type = "file";
		input.accept = ".csv";
		input.multiple = false;
		input.onchange = async (e) => {
			const files = (e.target as HTMLInputElement).files;
			if (files && files.length > 0 && files[0]) {
				const file = files[0];
				const contents = await file.text();
				const lines = contents.replace(/\r\n/g, "\n").split("\n");
				const parsedMembers: Partial<MemberDto>[] = [];

				for (const line of lines.slice(1, lines.length)) {
					const [first, last, email, phone, group] = line.split(",");
					parsedMembers.push({
						first_name: first?.trim() || "",
						last_name: last?.trim() || "",
						email: email?.trim() || "",
						phone: phone?.trim() || "",
						groups:
							group?.trim() && abbreviationToNameMap[group.trim() ?? ""]
								? // biome-ignore lint/style/noNonNullAssertion: checked above
									[abbreviationToNameMap[group.trim() ?? ""]!]
								: [],
					});
				}
				setInputMembers(parsedMembers);
				setPreviewOpen(true);
			}
		};
		input.click();
	};

	const handleFieldChange = (
		index: number,
		field: keyof Partial<MemberDto>,
		value: string,
	) => {
		setInputMembers((prev) => {
			const updated = [...prev];
			if (field === "groups") {
				updated[index] = {
					...updated[index],
					groups: value ? [value] : [],
				};
			} else {
				updated[index] = {
					...updated[index],
					[field]: value,
				};
			}
			return updated;
		});
	};

	const handleSave = () => {
		// Validate that all members have required fields
		const validMembers = inputMembers.filter(
			(member) =>
				member.first_name && member.last_name && member.email && member.phone,
		);

		if (validMembers.length === 0) {
			toast.error("No valid members to import");
			return;
		}

		if (validMembers.length < inputMembers.length) {
			toast.warning(
				`${inputMembers.length - validMembers.length} member(s) skipped due to missing required fields`,
			);
		}

		createBatch(
			validMembers as Array<
				Pick<
					MemberDto,
					"first_name" | "last_name" | "email" | "phone" | "groups"
				>
			>,
			{
				onSuccess: (data) => {
					const successCount = data.filter((r) => r.status === 200).length;
					const failCount = data.length - successCount;

					if (failCount > 0) {
						toast.error(
							`${successCount} member(s) imported, ${failCount} failed`,
						);
					} else {
						toast.success(`Successfully imported ${successCount} member(s)`);
					}
					setPreviewOpen(false);
					setInputMembers([]);
				},
				onError: (error) => {
					toast.error(`Failed to import members: ${error.message}`);
				},
			},
		);
	};

	return (
		<>
			<Button onClick={handleImport}>Import</Button>
			<AlertDialog open={isPreviewOpen}>
				<AlertDialogContent
					size="lg"
					className="max-h-[80vh] overflow-hidden flex flex-col"
				>
					<AlertDialogHeader>
						<AlertDialogTitle className="text-start w-full">
							Review Member Import Operation
						</AlertDialogTitle>
						<AlertDialogDescription className="text-left">
							Member information was pulled from the selected file. Once
							reviewed and necessary edits have been made click save to import
							the members.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<div className="overflow-auto flex-1">
						<div className="grid grid-cols-[1fr_1fr_2fr_1.5fr_1fr] gap-2 p-2 border-b font-semibold text-xs sticky top-0 bg-background">
							<div>First Name</div>
							<div>Last Name</div>
							<div>Email</div>
							<div>Phone</div>
							<div>Group</div>
						</div>
						<div className="space-y-2 p-2">
							{inputMembers.map((member, index) => (
								<div
									key={member.id}
									className="grid grid-cols-[.5fr_1fr_1fr_2fr_1.5fr_1fr] gap-2 items-center"
								>
									<p>{index + 1}</p>
									<Input
										value={member.first_name || ""}
										onChange={(e) =>
											handleFieldChange(index, "first_name", e.target.value)
										}
										placeholder="First name"
									/>
									<Input
										value={member.last_name || ""}
										onChange={(e) =>
											handleFieldChange(index, "last_name", e.target.value)
										}
										placeholder="Last name"
									/>
									<Input
										type="email"
										value={member.email || ""}
										onChange={(e) =>
											handleFieldChange(index, "email", e.target.value)
										}
										placeholder="Email"
									/>
									<Input
										type="tel"
										value={member.phone || ""}
										onChange={(e) =>
											handleFieldChange(index, "phone", e.target.value)
										}
										placeholder="Phone"
									/>
									<Select
										value={member.groups?.[0]}
										defaultValue=""
										onValueChange={(e) => {
											if (e) {
												handleFieldChange(index, "groups", e);
											}
										}}
									>
										<SelectTrigger className="w-[180px]">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value=""></SelectItem>
											{groups?.map((x) => (
												<SelectItem key={x.abbreviation} value={x.group_name}>
													{x.group_name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							))}
						</div>
					</div>
					<AlertDialogFooter>
						<AlertDialogCancel
							onClick={() => setPreviewOpen(false)}
							disabled={isPending}
						>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction onClick={handleSave} disabled={isPending}>
							{isPending ? "Saving..." : "Save"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
